import type { QueryParameters } from '@/hooks'
import type { LoginFormState } from '@workwave-tidal/core/login-ui'

import { useCallback } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'

import { type LoginThrottleFailure, login } from '@/features/actions/login'
import { useNavigate } from '@/routing'
import { useAppDispatch } from '@/store'
import { useNotification } from '@/hooks'
import { recoverPasswordService } from '@/services'
import { updateAccountDataToken, useEnvironmentURLs } from '@/atoms'

import { useActionsTexts } from './useActionsTexts'

export type LoginOutcome =
  | { outcome: 'success' }
  | { outcome: 'password-reset-required' }
  | { outcome: 'throttled'; failure: LoginThrottleFailure }
  | { outcome: 'error' }

export function useLoginActions(
  query: QueryParameters,
  onPasswordMatchesUsername: (username: string) => void,
) {
  const toast = useNotification()
  const texts = useActionsTexts()
  const dispatch = useAppDispatch()
  const navigate = useNavigate<uui.routing.MainSections>(true)
  const { serverUrl } = useEnvironmentURLs()

  const territoryId = query?.territoryId ?? undefined
  const simulationId = query?.simulationId ?? undefined

  const submitLogin = useCallback(
    async (data: LoginFormState): Promise<LoginOutcome> => {
      const { username, password /* rememberMe */ } = data

      try {
        const loginRequest = await dispatch(
          login({ username, password, serverUrl, territoryId, simulationId }),
        )

        if (login.fulfilled.match(loginRequest)) {
          const result = unwrapResult(loginRequest)

          // Now we're forcing rememberMe to TRUE, please replace with following line
          // when remember me will be managed correctly.
          //
          // updateAccountDataToken(username, result.authtoken, rememberMe)
          updateAccountDataToken(username, result.authtoken, true)
          return { outcome: 'success' }
        }

        if (loginRequest.payload?.type === 'LOGIN_ERROR_PASSWORD_MATCHES_USERNAME') {
          // the credentials are valid but the password must be reset before logging in
          onPasswordMatchesUsername(username)
          return { outcome: 'password-reset-required' }
        }

        // wrong credentials drive the inline throttling banners, not a snackbar
        const failure = loginRequest.payload?.throttle
        if (failure) {
          return { outcome: 'throttled', failure }
        }

        toast.error(texts.loginError(loginRequest.payload))
        return { outcome: 'error' }
      } catch (e) {
        // un-expected error
        navigate('error')
        return { outcome: 'error' }
      }
    },
    [
      dispatch,
      navigate,
      toast,
      serverUrl,
      texts,
      territoryId,
      simulationId,
      onPasswordMatchesUsername,
    ],
  )

  const forgotPassword = useCallback(
    async (data: { username: string }) => {
      const { username } = data

      try {
        await recoverPasswordService({ username, serverUrl })
        toast.success(texts.forgotPasswordSuccess)
      } catch (e) {
        toast.error(texts.forgotPasswordError)
      }
    },
    [texts, toast, serverUrl],
  )

  return { submitLogin, forgotPassword }
}
